import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Package, 
  Plus, 
  Search, 
  Edit,
  Trash2,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'reagent', label: 'Reagent' },
  { value: 'consumable', label: 'Consumable' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'chemical', label: 'Chemical' },
  { value: 'glassware', label: 'Glassware' },
  { value: 'other', label: 'Other' },
];

const AdminInventoryPage = () => {
  const { formatPrice } = useCurrency();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'reagent',
    quantity: 0,
    unit: 'units',
    min_quantity: 10,
    price_per_unit: 0,
    supplier: '',
    expiry_date: '',
    batch_number: '',
    storage_location: '',
    notes: ''
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/admin/inventory`);
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Item name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/inventory/${editingItem.id}`, formData);
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API}/admin/inventory`, formData);
        toast.success('Item added successfully');
      }
      setIsModalOpen(false);
      resetForm();
      fetchInventory();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/admin/inventory/${itemId}`);
      toast.success('Item deleted successfully');
      fetchInventory();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const openAddModal = () => {
    resetForm();
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      category: item.category || 'reagent',
      quantity: item.quantity || 0,
      unit: item.unit || 'units',
      min_quantity: item.min_quantity || 10,
      price_per_unit: item.price_per_unit || 0,
      supplier: item.supplier || '',
      expiry_date: item.expiry_date || '',
      batch_number: item.batch_number || '',
      storage_location: item.storage_location || '',
      notes: item.notes || ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'reagent',
      quantity: 0,
      unit: 'units',
      min_quantity: 10,
      price_per_unit: 0,
      supplier: '',
      expiry_date: '',
      batch_number: '',
      storage_location: '',
      notes: ''
    });
  };

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const lowStockItems = inventory.filter(item => item.quantity <= item.min_quantity);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-inventory-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Inventory Management</h1>
            <p className="text-muted-foreground mt-1">Manage reagents and lab supplies</p>
          </div>
          <Button onClick={openAddModal} className="bg-sky-600 hover:bg-sky-700" data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Low Stock Alert */}
        {lowStockItems.length > 0 && (
          <div className="card-clinical bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-300">Low Stock Alert</h3>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  {lowStockItems.length} item(s) are running low: {lowStockItems.map(i => i.name).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-foreground">{inventory.length}</p>
            <p className="text-sm text-muted-foreground">Total Items</p>
          </div>
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-emerald-600">{inventory.filter(i => i.quantity > i.min_quantity).length}</p>
            <p className="text-sm text-muted-foreground">In Stock</p>
          </div>
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-amber-600">{lowStockItems.length}</p>
            <p className="text-sm text-muted-foreground">Low Stock</p>
          </div>
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-red-600">{inventory.filter(i => i.quantity === 0).length}</p>
            <p className="text-sm text-muted-foreground">Out of Stock</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card-clinical">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-inventory-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="card-clinical overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner" />
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground">No Items Found</h3>
              <p className="text-muted-foreground mt-1">Start by adding inventory items</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Item</th>
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Category</th>
                    <th className="text-center py-4 px-4 font-medium text-muted-foreground">Quantity</th>
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Supplier</th>
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Expiry</th>
                    <th className="text-right py-4 px-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredInventory.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/50" data-testid={`inventory-row-${item.id}`}>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-foreground">{item.name}</p>
                          {item.batch_number && (
                            <p className="text-xs text-muted-foreground">Batch: {item.batch_number}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant="outline" className="capitalize">{item.category}</Badge>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`font-bold ${
                          item.quantity <= 0 ? 'text-red-600' : 
                          item.quantity <= item.min_quantity ? 'text-amber-600' : 'text-emerald-600'
                        }`}>
                          {item.quantity} {item.unit}
                        </span>
                        <p className="text-xs text-muted-foreground">Min: {item.min_quantity}</p>
                      </td>
                      <td className="py-4 px-4 text-muted-foreground">
                        {item.supplier || '-'}
                      </td>
                      <td className="py-4 px-4 text-muted-foreground">
                        {item.expiry_date || '-'}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditModal(item)}
                            data-testid={`edit-item-${item.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update inventory item details' : 'Add a new item to inventory'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Item Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter item name"
                  className="mt-1"
                  data-testid="item-name-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Input
                  value={formData.unit}
                  onChange={(e) => setFormData({...formData, unit: e.target.value})}
                  placeholder="e.g., units, ml, kg"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Min Quantity (Alert)</Label>
                <Input
                  type="number"
                  value={formData.min_quantity}
                  onChange={(e) => setFormData({...formData, min_quantity: parseInt(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Price per Unit (₦)</Label>
                <Input
                  type="number"
                  value={formData.price_per_unit}
                  onChange={(e) => setFormData({...formData, price_per_unit: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Supplier</Label>
                <Input
                  value={formData.supplier}
                  onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                  placeholder="Supplier name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Batch Number</Label>
                <Input
                  value={formData.batch_number}
                  onChange={(e) => setFormData({...formData, batch_number: e.target.value})}
                  placeholder="Batch #"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Storage Location</Label>
                <Input
                  value={formData.storage_location}
                  onChange={(e) => setFormData({...formData, storage_location: e.target.value})}
                  placeholder="e.g., Shelf A, Refrigerator 2"
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes..."
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="save-item-btn"
            >
              {submitting ? 'Saving...' : editingItem ? 'Update Item' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminInventoryPage;
